'use client';
import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

interface Client {
  name: string;
  industry: string;
  description: string;
  initials: string;
  size: string;
  location: string;
}

const clients: Client[] = [
  { name: 'GlobalTech Financial', industry: 'Financial Services', description: 'Placed 200+ IT professionals across cloud infrastructure, cybersecurity, and data analytics teams.', initials: 'GTF', size: 'Fortune 50', location: 'New York, NY' },
  { name: 'MedCore Health Systems', industry: 'Healthcare', description: 'Delivered specialized EHR developers, data scientists, and IT project managers for digital health initiatives.', initials: 'MHS', size: 'Fortune 100', location: 'Chicago, IL' },
  { name: 'RetailMax Corporation', industry: 'Retail & E-Commerce', description: 'Built entire e-commerce technology team including full-stack developers, DevOps engineers, and architects.', initials: 'RMC', size: 'Fortune 200', location: 'Seattle, WA' },
  { name: 'Apex Energy Group', industry: 'Energy & Utilities', description: 'Staffed SCADA engineers, IoT specialists, and cybersecurity professionals for critical infrastructure projects.', initials: 'AEG', size: 'Fortune 100', location: 'Houston, TX' },
  { name: 'NexGen Telecom', industry: 'Telecommunications', description: 'Provided network engineers, 5G specialists, and software developers for next-generation network deployment.', initials: 'NGT', size: 'Fortune 500', location: 'Dallas, TX' },
  { name: 'Pinnacle Insurance Group', industry: 'Insurance', description: 'Placed actuarial data scientists, claims automation developers, and enterprise architects for digital transformation.', initials: 'PIG', size: 'Fortune 300', location: 'Hartford, CT' },
  { name: 'AutoDrive Manufacturing', industry: 'Manufacturing', description: 'Delivered embedded systems engineers, robotics programmers, and MES specialists for smart factory initiatives.', initials: 'ADM', size: 'Fortune 200', location: 'Detroit, MI' },
  { name: 'FedGov Defense Systems', industry: 'Government & Defense', description: 'Provided cleared IT professionals, cybersecurity analysts, and systems engineers for classified government projects.', initials: 'FGD', size: 'Federal', location: 'Washington, DC' },
  { name: 'BioPharm Innovations', industry: 'Life Sciences', description: 'Staffed bioinformatics engineers, clinical data managers, and regulatory IT specialists for drug development platforms.', initials: 'BPI', size: 'Fortune 500', location: 'Boston, MA' },
  { name: 'CloudFirst Technologies', industry: 'Technology', description: 'Built cloud engineering teams, placed SRE specialists, and provided DevSecOps talent for hyperscale infrastructure.', initials: 'CFT', size: 'Fortune 100', location: 'San Francisco, CA' },
  { name: 'National Bank Corp', industry: 'Banking', description: 'Delivered fintech developers, blockchain engineers, and compliance technology specialists for digital banking transformation.', initials: 'NBC', size: 'Fortune 50', location: 'Charlotte, NC' },
  { name: 'LogiChain Supply Co', industry: 'Logistics', description: 'Placed supply chain technology architects, WMS developers, and AI/ML engineers for logistics optimization platforms.', initials: 'LCS', size: 'Fortune 400', location: 'Atlanta, GA' },
];

const ALL_INDUSTRIES = ['All Industries', ...Array.from(new Set(clients.map((c) => c.industry))).sort()];
const ALL_LOCATIONS = ['All Locations', ...Array.from(new Set(clients.map((c) => c.location))).sort()];
const ALL_SIZES = ['All Sizes', 'Fortune 50', 'Fortune 100', 'Fortune 200', 'Fortune 300', 'Fortune 400', 'Fortune 500', 'Federal'];

const industries = [
  { name: 'Financial Services', count: '85+' },
  { name: 'Healthcare', count: '70+' },
  { name: 'Technology', count: '95+' },
  { name: 'Retail', count: '60+' },
  { name: 'Energy', count: '45+' },
  { name: 'Government', count: '30+' },
  { name: 'Manufacturing', count: '55+' },
  { name: 'Telecom', count: '40+' },
];

export default function ClientsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('All Industries');
  const [selectedLocation, setSelectedLocation] = useState('All Locations');
  const [selectedSize, setSelectedSize] = useState('All Sizes');

  const filteredClients = useMemo(() => {
    return clients.filter((client) => {
      const matchesSearch =
        searchQuery === '' ||
        client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.industry.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesIndustry = selectedIndustry === 'All Industries' || client.industry === selectedIndustry;
      const matchesLocation = selectedLocation === 'All Locations' || client.location === selectedLocation;
      const matchesSize = selectedSize === 'All Sizes' || client.size === selectedSize;
      return matchesSearch && matchesIndustry && matchesLocation && matchesSize;
    });
  }, [searchQuery, selectedIndustry, selectedLocation, selectedSize]);

  const hasActiveFilters =
    searchQuery !== '' ||
    selectedIndustry !== 'All Industries' ||
    selectedLocation !== 'All Locations' ||
    selectedSize !== 'All Sizes';

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedIndustry('All Industries');
    setSelectedLocation('All Locations');
    setSelectedSize('All Sizes');
  };

  return (
    <div className="min-h-screen" style={{ background: '#ffffff' }}>
      <Navbar />

      {/* Page Hero - Pinnacle1 style */}
      <section
        className="relative overflow-hidden"
        style={{
          paddingTop: '144px',
          paddingBottom: '80px',
          background: 'linear-gradient(135deg, #01142D 0%, #0A1F3D 60%, #1F65AB 100%)',
        }}
      >
        <div
          className="absolute right-0 bottom-0 select-none pointer-events-none"
          style={{
            fontSize: '320px',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 700,
            color: 'rgba(0,167,255,0.05)',
            lineHeight: 1,
          }}
        >
          CL
        </div>
        <div className="container-custom relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <Link
              href="/"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#ffffff'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.5)'; }}
            >
              Home
            </Link>
            <span style={{ color: 'rgba(255,255,255,0.3)' }}>/</span>
            <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#00A7FF' }}>Clients</span>
          </div>
          <h1
            className="mb-4"
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: 'clamp(36px, 5vw, 64px)',
              fontWeight: 200,
              color: '#ffffff',
              lineHeight: 1.2,
            }}
          >
            Our Clients
          </h1>
          <div style={{ width: '60px', height: '3px', background: '#00A7FF', marginBottom: '24px' }}></div>
          <p
            className="max-w-2xl"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.7 }}
          >
            Trusted by 500+ Fortune 500 organizations across every major industry. We’re proud to be the IT staffing partner of choice for the world’s most innovative companies.
          </p>
        </div>
      </section>

      {/* Stats Bar - Pinnacle1 style: alternating blue/navy blocks */}
      <section style={{ background: '#ffffff', borderBottom: '1px solid #e8e8e8' }}>
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4">
            {[
              { value: '500+', label: 'Active Fortune 500 Clients', dark: false },
              { value: '12+', label: 'Industries Served', dark: true },
              { value: '98%', label: 'Client Retention Rate', dark: false },
              { value: '15K+', label: 'Successful Placements', dark: true },
            ].map((stat, i) => (
              <div
                key={stat.label}
                className="p-8 text-center"
                style={{ background: stat.dark ? '#01142D' : '#00A7FF' }}
              >
                <div
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '40px',
                    fontWeight: 200,
                    color: '#ffffff',
                    lineHeight: 1,
                    marginBottom: '8px',
                  }}
                >
                  {stat.value}
                </div>
                <div
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '12px',
                    fontWeight: 400,
                    color: 'rgba(255,255,255,0.8)',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section - Pinnacle1 style */}
      <section className="section-padding" style={{ background: '#FAFAFA' }}>
        <div className="container-custom">
          <div className="text-center mb-12">
            <h6
              className="mb-3"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1F65AB', letterSpacing: '0.1em', textTransform: 'uppercase' }}
            >
              Industries We Serve
            </h6>
            <h2
              className="mb-4"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '40px', fontWeight: 100, color: 'rgba(0,0,0,0.6)' }}
            >
              Expertise Across Every Sector
            </h2>
            <div style={{ width: '48px', height: '3px', background: '#00A7FF', margin: '0 auto' }}></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {industries.map((ind, i) => (
              <div
                key={ind.name}
                className="p-6 text-center transition-all duration-300"
                style={{
                  background: '#ffffff',
                  border: '1px solid #e8e8e8',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  borderTop: `3px solid ${i % 2 === 0 ? '#00A7FF' : '#01142D'}`,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 32px rgba(0,145,221,0.15)';
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                }}
              >
                <div
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '32px',
                    fontWeight: 200,
                    color: i % 2 === 0 ? '#00A7FF' : '#01142D',
                    lineHeight: 1,
                    marginBottom: '8px',
                  }}
                >
                  {ind.count}
                </div>
                <div
                  style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#48494B' }}
                >
                  {ind.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Grid with Search & Filters - Pinnacle1 style */}
      <section className="section-padding" style={{ background: '#ffffff' }}>
        <div className="container-custom">
          <div className="text-center mb-12">
            <h6
              className="mb-3"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1F65AB', letterSpacing: '0.1em', textTransform: 'uppercase' }}
            >
              Our Portfolio
            </h6>
            <h2
              className="mb-4"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '40px', fontWeight: 100, color: 'rgba(0,0,0,0.6)' }}
            >
              Companies We’ve Served
            </h2>
            <div style={{ width: '48px', height: '3px', background: '#00A7FF', margin: '0 auto 16px' }}></div>
            <p
              className="max-w-2xl mx-auto"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px', color: '#48494B', lineHeight: 1.7 }}
            >
              A representative sample of the Fortune 500 organizations that trust TranceformRIGHT to power their technology teams.
            </p>
          </div>

          {/* Search & Filter Bar */}
          <div
            className="p-6 mb-8"
            style={{ background: '#FAFAFA', border: '1px solid #e8e8e8' }}
          >
            <div className="relative mb-4">
              <svg
                className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5"
                style={{ color: '#888888' }}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search clients by name, industry, location, or keyword..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 text-sm focus:outline-none transition-colors"
                style={{
                  background: '#ffffff',
                  border: '1px solid #d1d5db',
                  fontFamily: 'Poppins, sans-serif',
                  color: '#48494B',
                }}
                onFocus={(e) => { (e.currentTarget as HTMLElement).style.borderColor = '#00A7FF'; }}
                onBlur={(e) => { (e.currentTarget as HTMLElement).style.borderColor = '#d1d5db'; }}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { label: 'Industry', value: selectedIndustry, options: ALL_INDUSTRIES, onChange: setSelectedIndustry },
                { label: 'Location', value: selectedLocation, options: ALL_LOCATIONS, onChange: setSelectedLocation },
                { label: 'Company Size', value: selectedSize, options: ALL_SIZES, onChange: setSelectedSize },
              ].map((filter) => (
                <div key={filter.label}>
                  <label
                    className="block mb-1.5"
                    style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '11px', fontWeight: 600, color: '#888888', letterSpacing: '0.1em', textTransform: 'uppercase' }}
                  >
                    {filter.label}
                  </label>
                  <div className="relative">
                    <select
                      value={filter.value}
                      onChange={(e) => filter.onChange(e.target.value)}
                      className="w-full appearance-none pl-4 pr-10 py-2.5 text-sm focus:outline-none cursor-pointer"
                      style={{
                        background: '#ffffff',
                        border: '1px solid #d1d5db',
                        fontFamily: 'Poppins, sans-serif',
                        color: '#48494B',
                      }}
                    >
                      {filter.options.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                    </select>
                    <svg
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                      style={{ color: '#888888' }}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>

            <div
              className="flex items-center justify-between mt-4 pt-4"
              style={{ borderTop: '1px solid #e8e8e8' }}
            >
              <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#888888' }}>
                Showing <strong style={{ color: '#48494B' }}>{filteredClients.length}</strong> of <strong style={{ color: '#48494B' }}>{clients.length}</strong> clients
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1.5 text-sm font-semibold transition-colors"
                  style={{ color: '#00A7FF', fontFamily: 'Poppins, sans-serif' }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Client Cards */}
          {filteredClients.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredClients.map((client, i) => (
                <div
                  key={client.name}
                  className="group transition-all duration-300"
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e8e8e8',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    borderTop: `3px solid ${i % 2 === 0 ? '#00A7FF' : '#01142D'}`,
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 32px rgba(0,145,221,0.15)';
                    (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
                    (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                  }}
                >
                  <div className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div
                        className="w-14 h-14 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 group-hover:scale-105 transition-transform duration-300"
                        style={{
                          background: i % 2 === 0 ? '#00A7FF' : '#01142D',
                          fontFamily: 'Poppins, sans-serif',
                          fontSize: '14px',
                        }}
                      >
                        {client.initials}
                      </div>
                      <div>
                        <h3
                          style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px', fontWeight: 400, color: 'rgba(0,0,0,0.8)' }}
                        >
                          {client.name}
                        </h3>
                        <div className="flex items-center flex-wrap gap-2 mt-1">
                          <span
                            className="px-2 py-0.5 text-xs"
                            style={{ background: 'rgba(0,167,255,0.1)', color: '#0091DD', fontFamily: 'Montserrat, sans-serif', fontSize: '11px', fontWeight: 600 }}
                          >
                            {client.industry}
                          </span>
                          <span
                            className="px-2 py-0.5 text-xs"
                            style={{ background: 'rgba(1,20,45,0.08)', color: '#01142D', fontFamily: 'Montserrat, sans-serif', fontSize: '11px', fontWeight: 600 }}
                          >
                            {client.size}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p
                      className="mb-3"
                      style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#48494B', lineHeight: 1.6 }}
                    >
                      {client.description}
                    </p>
                    <div className="flex items-center gap-1.5">
                      <svg
                        className="w-3.5 h-3.5 flex-shrink-0"
                        style={{ color: '#00A7FF' }}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: '#888888' }}>{client.location}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <svg
                className="w-16 h-16 mx-auto mb-4"
                style={{ color: '#d1d5db' }}
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <h3
                className="mb-2"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '22px', fontWeight: 400, color: 'rgba(0,0,0,0.6)' }}
              >
                No clients found
              </h3>
              <p
                className="mb-4"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '15px', color: '#888888' }}
              >
                Try adjusting your search or filter criteria.
              </p>
              <button
                onClick={clearFilters}
                className="px-6 py-3 font-semibold transition-all duration-300 hover:opacity-90"
                style={{ background: '#00A7FF', color: '#ffffff', fontFamily: 'Montserrat, sans-serif', fontSize: '14px' }}
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-20"
        style={{ background: 'linear-gradient(135deg, #01142D 0%, #0A1F3D 100%)' }}
      >
        <div className="container-custom text-center">
          <h2
            className="mb-4"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '40px', fontWeight: 200, color: '#ffffff', lineHeight: 1.3 }}
          >
            Join Our Growing Client Portfolio
          </h2>
          <div style={{ width: '48px', height: '3px', background: '#00A7FF', margin: '0 auto 24px' }}></div>
          <p
            className="mb-8 max-w-xl mx-auto"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7 }}
          >
            Discover how TranceformRIGHT can help your organization build the technology team that drives innovation and growth.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center justify-center px-10 py-4 font-semibold transition-all duration-300 hover:opacity-90"
            style={{ background: '#00A7FF', color: '#ffffff', fontFamily: 'Montserrat, sans-serif', fontSize: '15px' }}
          >
            Contact Us Today
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
